import {
  Markup,
  Tax,
  MarkupAmountType,
  TaxAmountType,
} from 'lane-shared/domains/billingPayments/types';
import {
  AdminFeeAmountType,
  AdminFee,
  AdminFeeTax,
  PriceComponents,
} from 'application-types';
import {
  getCurrencyPrecision,
  type SupportedPaymentCurrency,
} from 'domains-billing-currency';
import { formatCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';
import BigNumber from 'bignumber.js';

const PERCENTAGE_DIVISOR = 100;

export type PricingParams = {
  currency: SupportedPaymentCurrency;
  locale: SupportedLocaleEnum;
  priceComponents: PriceComponents;
  inclusivePricing?: boolean;
  isAdminFeeFeatureEnabled?: boolean;
};

export type Pricing = {
  rate: number; // Base amount
  grossMarkup: number; // Markup amount in unit currency
  grossTax: number; // Tax amount in unit currency
  grossAdminFee: number; // Admin fee amount in unit currency
  grossAdminFeeTax: number; // Tax on admin fee amount in unit currency
  listPrice: number; // Sum of rate, markup, and admin fee amounts in unit currency
  netPrice: number; // listPrice + grossTax + grossAdminFeeTax in unit currency
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string; // Formatter function that converts the price to a string
};

function isPercentageBased(
  optRate: Markup | Tax | AdminFee | AdminFeeTax
): boolean {
  if ('markupAmountType' in optRate) {
    return (
      optRate.markupAmountType === MarkupAmountType.MarkupAmountTypePercentage
    );
  }

  if ('taxAmountType' in optRate) {
    return optRate.taxAmountType === TaxAmountType.TaxAmountTypePercentage;
  }

  return (
    optRate.adminFeeAmountType ===
    AdminFeeAmountType.AdminFeeAmountTypePercentage
  );
}

export function calculateExclusiveOptionalRate(
  rate: BigNumber,
  optRate: Markup | Tax | AdminFee | AdminFeeTax
) {
  const optValue = 'value' in optRate ? optRate.value : optRate.rate;
  const isPercentage = isPercentageBased(optRate);

  const calculatedValueForPercentage = Number(
    rate.multipliedBy(optValue).dividedBy(PERCENTAGE_DIVISOR).toFixed(2)
  );

  return isPercentage ? calculatedValueForPercentage || 0 : optValue || 0;
}

export function calculateInclusiveOptionalRate(
  price: BigNumber,
  rate: BigNumber
) {
  return price.minus(rate).toNumber() || 0;
}

export function calculateBaseRateFromInclusiveAmount(
  price: BigNumber,
  optRate: Markup | Tax | AdminFee
) {
  const { value } = optRate;
  const isPercentage = isPercentageBased(optRate);

  return isPercentage
    ? price.dividedBy(1 + value / PERCENTAGE_DIVISOR).toNumber() || 0
    : price.minus(value).toNumber() || 0;
}

export function getPricing({
  currency,
  locale,
  priceComponents,
  inclusivePricing = false,
  isAdminFeeFeatureEnabled = false,
}: PricingParams): Pricing {
  const { sellingPrice, amount } = priceComponents;

  const precision = getCurrencyPrecision(currency);

  const formatPrice = (
    amount: number,
    currencyDisplay: 'narrowSymbol' | 'none' = 'none'
  ) =>
    formatCurrency(currency, {
      locale,
      currencyDisplay,
      useGrouping: false,
    })(amount);

  let rate = 0;
  let grossMarkup = 0;
  let grossTax = 0;
  let grossAdminFee = 0;
  let grossAdminFeeTax = 0;
  let listPrice = 0;

  if (
    sellingPrice?.listPrice !== undefined &&
    sellingPrice.grossTax !== undefined &&
    sellingPrice.netPrice !== undefined &&
    sellingPrice.grossMarkup !== undefined
  ) {
    listPrice = Number(formatPrice(sellingPrice.listPrice));
    grossTax = Number(formatPrice(sellingPrice.grossTax));
    rate = Number(formatPrice(amount));
    grossMarkup = Number(formatPrice(sellingPrice.grossMarkup));
    grossAdminFee = Number(formatPrice(sellingPrice.grossAdminFee ?? 0));
    grossAdminFeeTax = Number(formatPrice(sellingPrice.grossAdminFeeTax ?? 0));
  } else if (sellingPrice?.listPrice !== undefined) {
    ({
      listPrice,
      rate,
      grossMarkup,
      grossTax,
      grossAdminFee,
      grossAdminFeeTax,
    } = calculateListPriceOverridePricing(
      priceComponents,
      formatPrice,
      precision,
      isAdminFeeFeatureEnabled
    ));
  } else if (inclusivePricing) {
    ({
      listPrice,
      grossTax,
      rate,
      grossMarkup,
      grossAdminFee,
      grossAdminFeeTax,
    } = (
      isAdminFeeFeatureEnabled
        ? calculateInclusivePricingWithRateFees
        : calculateInclusivePricing
    )(priceComponents, formatPrice, precision));
  } else {
    ({
      listPrice,
      grossTax,
      rate,
      grossMarkup,
      grossAdminFee,
      grossAdminFeeTax,
    } = (
      isAdminFeeFeatureEnabled
        ? calculateExclusivePricingWithRateFees
        : calculateExclusivePricing
    )(priceComponents, formatPrice, precision));
  }

  const netPrice = new BigNumber(listPrice).plus(grossTax).plus(grossAdminFeeTax).toNumber();

  return {
    rate,
    grossMarkup,
    grossTax,
    grossAdminFee,
    grossAdminFeeTax,
    listPrice,
    netPrice,
    formatPrice,
  };
}

function calculateListPriceOverridePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number,
  _isAdminFeeFeatureEnabled: boolean
) {
  const { sellingPrice, amount } = priceComponents;

  const listPrice = Number(formatPrice(sellingPrice!.listPrice || 0));
  const rate = Number(formatPrice(amount));
  const grossMarkup = Number(formatPrice(sellingPrice!.grossMarkup || 0));
  const grossAdminFee = Number(formatPrice(sellingPrice!.grossAdminFee || 0));
  const grossAdminFeeTax = Number(
    formatPrice(sellingPrice!.grossAdminFeeTax || 0)
  );

  const taxableAmount = new BigNumber(listPrice);

  const grossTax = new BigNumber(
    priceComponents.tax
      ? Number(
          formatPrice(
            calculateExclusiveOptionalRate(taxableAmount, priceComponents.tax)
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    rate,
    grossMarkup,
    grossAdminFee,
    grossAdminFeeTax,
    grossTax,
  };
}

function calculateInclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, netPrice } = priceComponents;

  const listPrice = new BigNumber(
    tax
      ? Number(
          formatPrice(
            calculateBaseRateFromInclusiveAmount(
              new BigNumber(netPrice ?? 0),
              tax
            )
          )
        )
      : (netPrice ?? 0)
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossTax = new BigNumber(
    tax
      ? Number(
          formatPrice(
            calculateInclusiveOptionalRate(
              new BigNumber(netPrice ?? 0),
              new BigNumber(listPrice)
            )
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const rate = new BigNumber(
    markup
      ? Number(
          formatPrice(
            calculateBaseRateFromInclusiveAmount(
              new BigNumber(listPrice),
              markup
            )
          )
        )
      : listPrice
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
          formatPrice(
            calculateInclusiveOptionalRate(
              new BigNumber(listPrice),
              new BigNumber(rate)
            )
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee: 0,
    grossAdminFeeTax: 0,
  };
}

// Types for inclusive pricing helpers
type FormatPriceFn = (
  amount: number,
  currencyDisplay?: 'narrowSymbol' | 'none'
) => string;

type InclusiveRateConfig = {
  markupRate: number;
  taxRate: number;
  adminFeeRate: number;
  adminFeeTaxRate: number;
  hasMarkup: boolean;
  hasAdminFee: boolean;
  hasTax: boolean;
  hasSeparateAdminFeeTax: boolean;
  markupIsPercentage: boolean;
  adminFeeIsPercentage: boolean;
  adminFeeTaxIsPercentage: boolean;
  taxIsPercentage: boolean;
  fixedMarkup: number;
  fixedAdminFee: number;
  fixedAdminFeeTax: number;
  fixedTax: number;
};

type InclusivePricingResult = {
  rate: number;
  grossAdminFee: number;
  grossAdminFeeTax: number;
  grossTax: number;
  grossMarkup: number;
};

/**
 * Splits fixed tax proportionally between rate+markup and admin fee.
 * Calculates grossAdminFeeTax proportionally, then plugs grossTax to ensure
 * grossTax + grossAdminFeeTax = fixedTax exactly.
 *
 * @param fixedTax - The total fixed tax amount to split
 * @param rateAndMarkup - Combined rate and markup amount
 * @param adminFee - Admin fee amount
 * @param formatPrice - Price formatting function
 * @param precision - Decimal precision
 * @returns Object containing grossTax and grossAdminFeeTax
 */
function splitFixedTaxWithGrossTaxPlug(
  fixedTax: number,
  rateAndMarkup: BigNumber,
  adminFee: BigNumber,
  formatPrice: FormatPriceFn,
  precision: number
): { grossTax: number; grossAdminFeeTax: number } {
  const total = rateAndMarkup.plus(adminFee);

  if (total.isZero()) {
    return { grossTax: 0, grossAdminFeeTax: 0 };
  }

  // Calculate grossAdminFeeTax proportionally: adminFee's share of fixedTax
  const grossAdminFeeTax = new BigNumber(
    Number(
      formatPrice(
        adminFee.dividedBy(total).multipliedBy(fixedTax).toNumber()
      )
    )
  )
    .decimalPlaces(precision)
    .toNumber();

  // Plug grossTax: fixedTax - grossAdminFeeTax (ensures tax components sum to fixedTax)
  const grossTax = new BigNumber(fixedTax)
    .minus(grossAdminFeeTax)
    .decimalPlaces(precision)
    .toNumber();

  return { grossTax, grossAdminFeeTax };
}

function isAdminFeeTaxPercentage(
  adminFeeTax: AdminFeeTax | undefined
): boolean {
  return (
    !!adminFeeTax &&
    adminFeeTax.taxAmountType === TaxAmountType.TaxAmountTypePercentage
  );
}

function extractInclusiveRateConfig(
  priceComponents: PriceComponents
): InclusiveRateConfig {
  const { markup, adminFee, adminFeeTax, tax } = priceComponents;

  const taxRate = tax && isPercentageBased(tax) ? tax.value / PERCENTAGE_DIVISOR : 0;
  const hasSeparateAdminFeeTax = adminFeeTax && adminFeeTax.rate >= 0;

  let adminFeeTaxRate: number;
  let adminFeeTaxIsPercentage: boolean;
  let fixedAdminFeeTax: number;

  if (hasSeparateAdminFeeTax) {
    adminFeeTaxRate = isAdminFeeTaxPercentage(adminFeeTax)
      ? adminFeeTax!.rate / PERCENTAGE_DIVISOR
      : 0;
    adminFeeTaxIsPercentage = isAdminFeeTaxPercentage(adminFeeTax);
    fixedAdminFeeTax =
      adminFeeTax && !isAdminFeeTaxPercentage(adminFeeTax)
        ? adminFeeTax.rate || 0
        : 0;
  } else {
    adminFeeTaxRate = taxRate;
    adminFeeTaxIsPercentage = !!(tax && isPercentageBased(tax));
    fixedAdminFeeTax = 0;
  }

  const hasAdminFee = !!(adminFee && adminFee.value > 0);

  return {
    markupRate: markup && isPercentageBased(markup) ? markup.value / PERCENTAGE_DIVISOR : 0,
    taxRate,
    adminFeeRate:
      adminFee && isPercentageBased(adminFee) ? adminFee.value / PERCENTAGE_DIVISOR : 0,
    adminFeeTaxRate,
    hasMarkup: !!(markup && markup.value > 0),
    hasAdminFee,
    hasTax: !!(tax && tax.value > 0),
    hasSeparateAdminFeeTax: !!hasSeparateAdminFeeTax,
    markupIsPercentage: !!(markup && isPercentageBased(markup)),
    adminFeeIsPercentage: !!(adminFee && isPercentageBased(adminFee)),
    adminFeeTaxIsPercentage,
    taxIsPercentage: !!(tax && isPercentageBased(tax)),
    fixedMarkup: markup && !isPercentageBased(markup) ? markup.value || 0 : 0,
    fixedAdminFee:
      adminFee && !isPercentageBased(adminFee) ? adminFee.value || 0 : 0,
    fixedAdminFeeTax,
    fixedTax: tax && !isPercentageBased(tax) ? tax.value || 0 : 0,
  };
}

function calculateInclusiveDivisor(
  config: InclusiveRateConfig,
  withMarkup: boolean
): number {
  const { markupRate, taxRate, adminFeeRate, adminFeeTaxRate } = config;

  if (withMarkup) {
    return (
      (1 + markupRate) * (1 + taxRate) + adminFeeRate * (1 + adminFeeTaxRate)
    );
  }

  return adminFeeRate > 0
    ? 1 + taxRate + adminFeeRate * (1 + adminFeeTaxRate)
    : 1 + taxRate;
}

function calculateInclusiveWithMarkupPlug(
  netPriceValue: BigNumber,
  config: InclusiveRateConfig,
  formatPrice: FormatPriceFn,
  precision: number
): InclusivePricingResult {
  const {
    markupRate,
    taxRate,
    adminFeeRate,
    hasTax,
    taxIsPercentage,
    fixedTax,
    hasAdminFee,
    adminFeeIsPercentage,
    fixedAdminFee,
    adminFeeTaxIsPercentage,
    fixedAdminFeeTax,
  } = config;

  const divisor = calculateInclusiveDivisor(config, true);

  // Fixed amounts are not captured in the divisor and must be subtracted
  // before dividing to find the base rate
  let amountForCalculation = netPriceValue;

  if (!adminFeeIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedAdminFee);
  }

  if (hasAdminFee && !adminFeeTaxIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedAdminFeeTax);
  }

  if (!taxIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedTax);
  }

  // Step 1: Base (anchor) = (Total - fixedFees) / divisor
  const rate = new BigNumber(
    Number(formatPrice(amountForCalculation.dividedBy(divisor).toNumber()))
  )
    .decimalPlaces(precision)
    .toNumber();

  // Step 2: Admin Fee = Base × adminFeeRate (or fixed)
  let grossAdminFeeValue = 0;

  if (hasAdminFee && adminFeeIsPercentage) {
    grossAdminFeeValue = Number(
      formatPrice(new BigNumber(rate).multipliedBy(adminFeeRate).toNumber())
    );
  } else if (hasAdminFee) {
    grossAdminFeeValue = Number(formatPrice(fixedAdminFee));
  }

  const grossAdminFee = new BigNumber(grossAdminFeeValue)
    .decimalPlaces(precision)
    .toNumber();

  // Step 3: Admin Fee Tax = Admin Fee × adminFeeTaxRate (percentage) or fixed amount
  const { adminFeeTaxRate } = config;
  let grossAdminFeeTaxValue = 0;

  if (hasAdminFee && adminFeeTaxIsPercentage) {
    grossAdminFeeTaxValue = Number(
      formatPrice(
        new BigNumber(grossAdminFee).multipliedBy(adminFeeTaxRate).toNumber()
      )
    );
  } else if (hasAdminFee) {
    grossAdminFeeTaxValue = Number(formatPrice(fixedAdminFeeTax));
  }

  const grossAdminFeeTax = new BigNumber(grossAdminFeeTaxValue)
    .decimalPlaces(precision)
    .toNumber();

  // Step 4: Sales Tax = (Base + Markup_estimated) × taxRate (percentage) or fixed amount
  const markupEstimated = new BigNumber(rate).multipliedBy(markupRate);
  let grossTaxValue = 0;

  if (hasTax && taxIsPercentage) {
    grossTaxValue = Number(
      formatPrice(
        new BigNumber(rate)
          .plus(markupEstimated)
          .multipliedBy(taxRate)
          .toNumber()
      )
    );
  } else if (hasTax) {
    grossTaxValue = Number(formatPrice(fixedTax));
  }

  let grossTax = new BigNumber(grossTaxValue)
    .decimalPlaces(precision)
    .toNumber();

  let adminFeeTax = grossAdminFeeTax;

  const shouldSplitFixedTax =
    !config.taxIsPercentage &&
    config.fixedTax > 0 &&
    !config.hasSeparateAdminFeeTax;

  if (shouldSplitFixedTax) {
    const baseWithMarkup = new BigNumber(rate).plus(markupEstimated);
    const adminFeeAmount = new BigNumber(grossAdminFee);

    const split = splitFixedTaxWithGrossTaxPlug(
      fixedTax,
      baseWithMarkup,
      adminFeeAmount,
      formatPrice,
      precision
    );

    grossTax = split.grossTax;
    adminFeeTax = split.grossAdminFeeTax;
  }

  // Step 5: Markup = Total - Base - AdminFee - AdminFeeTax - Tax (plug absorbs rounding)
  const grossMarkup = new BigNumber(
    Number(
      formatPrice(
        netPriceValue
          .minus(rate)
          .minus(grossAdminFee)
          .minus(adminFeeTax)
          .minus(grossTax)
          .toNumber()
      )
    )
  )
    .decimalPlaces(precision)
    .toNumber();

  return { rate, grossAdminFee, grossAdminFeeTax: adminFeeTax, grossTax, grossMarkup };
}

function calculateInclusiveWithAdminFeePlug(
  netPriceValue: BigNumber,
  config: InclusiveRateConfig,
  formatPrice: FormatPriceFn,
  precision: number
): InclusivePricingResult {
  const {
    taxRate,
    hasTax,
    taxIsPercentage,
    fixedTax,
    hasMarkup,
    hasAdminFee,
    fixedMarkup,
    fixedAdminFee,
    adminFeeTaxIsPercentage,
    fixedAdminFeeTax,
  } = config;

  const divisor = calculateInclusiveDivisor(config, false);

  // Step 1: Base = (Total - fixedFees) / divisor
  // Only percentage rates are captured in the divisor; all fixed amounts must be subtracted
  const taxOnFixedMarkup = taxIsPercentage
    ? new BigNumber(fixedMarkup).multipliedBy(taxRate)
    : new BigNumber(0);

  let baseBeforeFixedFees = netPriceValue
    .minus(fixedMarkup)
    .minus(fixedAdminFee)
    .minus(fixedTax)
    .minus(taxOnFixedMarkup);

  if (hasAdminFee && !adminFeeTaxIsPercentage) {
    baseBeforeFixedFees = baseBeforeFixedFees.minus(fixedAdminFeeTax);
  }

  const rate = new BigNumber(
    Number(formatPrice(baseBeforeFixedFees.dividedBy(divisor).toNumber()))
  )
    .decimalPlaces(precision)
    .toNumber();

  // Step 2: Sales Tax = (Base + fixedMarkup) × taxRate (percentage) or fixed amount
  // Tax applies to both rate and markup, consistent with other pricing paths
  let grossTaxValue = 0;

  if (hasTax && taxIsPercentage && !hasAdminFee) {
    grossTaxValue = netPriceValue
      .minus(rate)
      .minus(hasMarkup ? Number(formatPrice(fixedMarkup)) : 0)
      .decimalPlaces(precision)
      .toNumber();
  } else if (hasTax && taxIsPercentage) {
    grossTaxValue = Number(
      formatPrice(
        new BigNumber(rate).plus(fixedMarkup).multipliedBy(taxRate).toNumber()
      )
    );
  } else if (hasTax) {
    grossTaxValue = Number(formatPrice(fixedTax));
  }

  let grossTax = new BigNumber(grossTaxValue)
    .decimalPlaces(precision)
    .toNumber();

  // Step 3: Markup (fixed or 0)
  const grossMarkup = new BigNumber(
    hasMarkup ? Number(formatPrice(fixedMarkup)) : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  // Step 4: Admin Fee + Admin Fee Tax = Total - Base - Tax - Markup (plug absorbs rounding)
  const { adminFeeTaxRate } = config;

  const adminFeeWithAdminFeeTax = netPriceValue
    .minus(rate)
    .minus(grossTax)
    .minus(grossMarkup);

  let grossAdminFee: number;
  let grossAdminFeeTax: number;

  const shouldSplitFixedTax =
    !config.taxIsPercentage &&
    config.fixedTax > 0 &&
    !config.hasSeparateAdminFeeTax;

  if (shouldSplitFixedTax) {
    grossAdminFee = new BigNumber(
      Number(formatPrice(adminFeeWithAdminFeeTax.toNumber()))
    )
      .decimalPlaces(precision)
      .toNumber();

    const baseWithMarkup = new BigNumber(rate).plus(grossMarkup);
    const adminFeeAmount = new BigNumber(grossAdminFee);

    const split = splitFixedTaxWithGrossTaxPlug(
      fixedTax,
      baseWithMarkup,
      adminFeeAmount,
      formatPrice,
      precision
    );

    grossTax = split.grossTax;
    grossAdminFeeTax = split.grossAdminFeeTax;
  } else if (hasAdminFee && adminFeeTaxIsPercentage) {
    grossAdminFee = new BigNumber(
      Number(
        formatPrice(adminFeeWithAdminFeeTax.dividedBy(1 + adminFeeTaxRate).toNumber())
      )
    )
      .decimalPlaces(precision)
      .toNumber();
    grossAdminFeeTax = new BigNumber(
      Number(formatPrice(adminFeeWithAdminFeeTax.minus(grossAdminFee).toNumber()))
    )
      .decimalPlaces(precision)
      .toNumber();
  } else if (hasAdminFee) {
    grossAdminFeeTax = new BigNumber(Number(formatPrice(fixedAdminFeeTax)))
      .decimalPlaces(precision)
      .toNumber();

    grossAdminFee = new BigNumber(
      Number(formatPrice(adminFeeWithAdminFeeTax.minus(grossAdminFeeTax).toNumber()))
    )
      .decimalPlaces(precision)
      .toNumber();
  } else {
    grossAdminFee = new BigNumber(
      hasAdminFee ? Number(formatPrice(adminFeeWithAdminFeeTax.toNumber())) : 0
    )
      .decimalPlaces(precision)
      .toNumber();
    grossAdminFeeTax = 0;
  }

  return { rate, grossAdminFee, grossAdminFeeTax, grossTax, grossMarkup };
}

function calculateInclusivePricingWithRateFees(
  priceComponents: PriceComponents,
  formatPrice: FormatPriceFn,
  precision: number
) {
  const netPriceValue = new BigNumber(priceComponents.netPrice ?? 0);

  if (netPriceValue.isZero() || netPriceValue.isNaN()) {
    return {
      listPrice: 0,
      grossTax: 0,
      rate: 0,
      grossMarkup: 0,
      grossAdminFee: 0,
      grossAdminFeeTax: 0,
    };
  }

  const config = extractInclusiveRateConfig(priceComponents);

  const { rate, grossAdminFee, grossAdminFeeTax, grossTax, grossMarkup } =
    config.hasMarkup && config.markupIsPercentage
      ? calculateInclusiveWithMarkupPlug(
          netPriceValue,
          config,
          formatPrice,
          precision
        )
      : calculateInclusiveWithAdminFeePlug(
          netPriceValue,
          config,
          formatPrice,
          precision
        );

  const listPrice = new BigNumber(rate)
    .plus(grossMarkup)
    .plus(grossAdminFee)
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee,
    grossAdminFeeTax,
  };
}

function calculateExclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, amount } = priceComponents;

  const rate = new BigNumber(amount || 0).decimalPlaces(precision).toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
          formatPrice(
            calculateExclusiveOptionalRate(new BigNumber(amount), markup)
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const listPrice =
    new BigNumber(amount)
      .plus(grossMarkup)
      .decimalPlaces(precision)
      .toNumber() || 0;

  const grossTax = new BigNumber(
    tax
      ? Number(
          formatPrice(
            calculateExclusiveOptionalRate(new BigNumber(listPrice), tax)
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee: 0,
    grossAdminFeeTax: 0,
  };
}

function calculateExclusivePricingWithRateFees(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, adminFee, tax, amount, adminFeeTax } = priceComponents;

  const rate = new BigNumber(amount || 0).decimalPlaces(precision).toNumber();

  let grossMarkupValue = 0;

  if (markup) {
    const rawMarkup = calculateExclusiveOptionalRate(
      new BigNumber(amount),
      markup
    );

    grossMarkupValue = Number(formatPrice(rawMarkup));
  }

  const grossMarkup = new BigNumber(grossMarkupValue)
    .decimalPlaces(precision)
    .toNumber();

  let grossAdminFeeValue = 0;
  let grossAdminFeeTaxValue = 0;

  if (adminFee) {
    const rawAdminFee = calculateExclusiveOptionalRate(
      new BigNumber(amount),
      adminFee
    );

    grossAdminFeeValue = Number(formatPrice(rawAdminFee));
  }

  const grossAdminFee = new BigNumber(grossAdminFeeValue)
    .decimalPlaces(precision)
    .toNumber();

  const hasSeparateAdminFeeTax = adminFeeTax && adminFeeTax.rate >= 0;
  const taxIsFixed = tax && !isPercentageBased(tax);
  const shouldSplitFixedTax = taxIsFixed && !hasSeparateAdminFeeTax;

  if (grossAdminFee !== 0 && !shouldSplitFixedTax) {
    if (hasSeparateAdminFeeTax) {
      const rawAdminFeeTax = calculateExclusiveOptionalRate(
        new BigNumber(grossAdminFee),
        adminFeeTax!
      );

      grossAdminFeeTaxValue = Number(formatPrice(rawAdminFeeTax));
    } else if (tax) {
      const rawAdminFeeTax = calculateExclusiveOptionalRate(
        new BigNumber(grossAdminFee),
        tax
      );

      grossAdminFeeTaxValue = Number(formatPrice(rawAdminFeeTax));
    }
  }

  let grossAdminFeeTax = new BigNumber(grossAdminFeeTaxValue)
    .decimalPlaces(precision)
    .toNumber();

  const listPrice =
    new BigNumber(amount)
      .plus(grossMarkup)
      .plus(grossAdminFee)
      .decimalPlaces(precision)
      .toNumber() || 0;

  const taxableAmount = new BigNumber(amount).plus(grossMarkup);
  let grossTaxValue = 0;

  if (tax) {
    const rawTax = calculateExclusiveOptionalRate(taxableAmount, tax);

    grossTaxValue = Number(formatPrice(rawTax));
  }

  let grossTax = new BigNumber(grossTaxValue)
    .decimalPlaces(precision)
    .toNumber();

  if (shouldSplitFixedTax && tax) {
    const baseWithMarkup = new BigNumber(rate).plus(grossMarkup);
    const adminFeeAmount = new BigNumber(grossAdminFee);

    const split = splitFixedTaxWithGrossTaxPlug(
      tax.value,
      baseWithMarkup,
      adminFeeAmount,
      formatPrice,
      precision
    );

    grossTax = split.grossTax;
    grossAdminFeeTax = split.grossAdminFeeTax;
  }

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee,
    grossAdminFeeTax,
  };
}
